import { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/crmService';
import CompetitorLogoUpload from './CompetitorLogoUpload';

const TYPE_META = {
  direct:        { color: '#EF4444', label: 'Direct',        threat: 'High' },
  adjacent:      { color: '#F59E0B', label: 'Adjacent',      threat: 'Medium' },
  complementary: { color: '#3B82F6', label: 'Complementary', threat: 'Low' },
  partner:       { color: '#10B981', label: 'Partner',       threat: 'Ally' },
};

const FUNDING_STAGE_META = {
  seed:         { label: 'Seed',         color: '#EAB308' },
  series_a:     { label: 'Series A',    color: '#3B82F6' },
  series_b:     { label: 'Series B',    color: '#2563EB' },
  series_c:     { label: 'Series C',    color: '#1D4ED8' },
  series_d_plus:{ label: 'Series D+',   color: '#1E40AF' },
  sbir_phase1:  { label: 'SBIR Ph.I',   color: '#64748B' },
  sbir_phase2:  { label: 'SBIR Ph.II',  color: '#22D3EE' },
  ota:          { label: 'OTA',         color: '#8B5CF6' },
  pre_ipo:      { label: 'Pre-IPO',     color: '#7C3AED' },
};

const ALIGNMENT_META = {
  five_eyes:  { bg: '#22D3EE', text: '#0A0F1C', label: 'Five Eyes' },
  nato_ally:  { bg: '#3B82F6', text: '#ffffff', label: 'NATO' },
  friendly:   { bg: '#10B981', text: '#ffffff', label: 'Friendly' },
  neutral:    { bg: '#6B7280', text: '#ffffff', label: 'Neutral' },
  unfriendly: { bg: '#F59E0B', text: '#0A0F1C', label: 'Caution' },
  adversary:  { bg: '#EF4444', text: '#ffffff', label: 'Adversary' },
};

const inputStyle = {
  background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9',
  padding: '6px 10px', borderRadius: '4px', fontSize: '12px', width: '100%',
};
const labelStyle = { color: '#94A3B8', fontSize: '11px', display: 'block', marginBottom: '4px' };

function formatRevenue(millions) {
  if (millions == null) return '—';
  if (millions >= 1000) return `$${(millions / 1000).toFixed(1)}B`;
  return `$${Math.round(millions)}M`;
}

function logoUrl(website) {
  if (!website) return null;
  try {
    const domain = new URL(website).hostname.replace(/^www\./, '');
    return `https://logo.clearbit.com/${domain}`;
  } catch { return null; }
}

function CompanyLogo({ website, name, uploadedLogo }) {
  const [errored, setErrored] = useState(false);

  // Priority: uploaded logo > Clearbit > initials
  const url = uploadedLogo || logoUrl(website);

  if (!url || errored) {
    return (
      <div style={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        background: '#111827', border: '1px solid #1E2D45',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#22D3EE', fontSize: '13px', fontWeight: '700',
      }}>
        {(name || '?')[0].toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      onError={() => setErrored(true)}
      style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid #1E2D45' }}
    />
  );
}

function AlignmentBadge({ alignment }) {
  const meta = ALIGNMENT_META[alignment];
  if (!meta) return null;
  return (
    <span style={{
      background: meta.bg, color: meta.text,
      padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '700',
      letterSpacing: '0.03em', flexShrink: 0,
    }}>{meta.label}</span>
  );
}

function CompetitorModal({ competitor, onSuccess, onClose }) {
  const [form, setForm] = useState({
    name: '', acronym: '', website: '', relationship_type: 'direct',
    description: '', strengths: '', weaknesses: '', win_rate_against: '', notes: '',
    ...competitor,
    product_line_overlap: competitor
      ? (Array.isArray(competitor.product_line_overlap)
          ? competitor.product_line_overlap.join(', ')
          : competitor.product_line_overlap || '')
      : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ingest, setIngest] = useState(null); // null | { status, products_count, total_products, message }

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  // Kick off the website product ingest for a newly-added company and poll its job status.
  const startIngest = async (id) => {
    setIngest({ status: 'running' });
    try {
      await crmService.ingestCompetitorProducts(id);
    } catch (e) {
      setIngest({ status: 'error', message: e.message });
      return;
    }
    const deadline = Date.now() + 3 * 60 * 1000;
    const tick = async () => {
      try {
        const s = await crmService.getCompetitorIngestStatus(id);
        setIngest(s);
        if (['done', 'error', 'none'].includes(s.status)) return;
      } catch (e) {
        setIngest({ status: 'error', message: e.message });
        return;
      }
      if (Date.now() < deadline) setTimeout(tick, 3000);
    };
    setTimeout(tick, 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        win_rate_against: form.win_rate_against !== '' ? parseInt(form.win_rate_against) : null,
        product_line_overlap: form.product_line_overlap
          ? form.product_line_overlap.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      if (competitor?.id) {
        await crmService.updateCompetitor(competitor.id, payload);
        onSuccess?.();
        onClose();
      } else {
        // Use manual competitor endpoint for new competitors added via UI
        const res = await crmService.addManualCompetitor({
          name: payload.name,
          description: payload.description,
          website: payload.website,
          relationship_type: payload.relationship_type,
          notes: payload.notes || null
        });
        onSuccess?.();
        const newId = res?.competitor?.id;
        // If it has a website, ingest its public product catalog and show progress here;
        // otherwise close as before.
        if (newId && payload.website) {
          setSaving(false);
          startIngest(newId);
          return;
        }
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modal = { background: '#111827', border: '1px solid #1E2D45', borderRadius: '8px', padding: '24px', width: 'min(520px, 90vw)', maxHeight: '90vh', overflowY: 'auto', margin: '0 auto' };
  const row2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <h3 style={{ color: '#22D3EE', margin: '0 0 20px', fontSize: '16px' }}>
          {competitor?.id ? 'Edit Company' : 'Add Company'}
        </h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #1E2D45', display: 'flex', justifyContent: 'center' }}>
            <CompetitorLogoUpload
              competitorId={competitor?.id || null}
              currentLogo={competitor?.logo_url || null}
              onLogoUpdated={() => { /* Already saved to DB */ }}
            />
          </div>
          <div style={row2}>
            <div><label style={labelStyle}>Name *</label><input style={inputStyle} value={form.name} onChange={set('name')} required /></div>
            <div><label style={labelStyle}>Acronym</label><input style={inputStyle} value={form.acronym} onChange={set('acronym')} /></div>
          </div>
          <div style={row2}>
            <div>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={form.relationship_type} onChange={set('relationship_type')}>
                <option value="direct">Direct</option>
                <option value="adjacent">Adjacent</option>
                <option value="complementary">Complementary</option>
                <option value="partner">Partner</option>
              </select>
            </div>
            <div><label style={labelStyle}>Win Rate Against (0–100)</label><input style={inputStyle} type="number" min="0" max="100" value={form.win_rate_against} onChange={set('win_rate_against')} placeholder="e.g. 65" /></div>
          </div>
          <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Website</label><input style={inputStyle} value={form.website} onChange={set('website')} placeholder="https://…" /></div>
          <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Description</label><textarea style={{ ...inputStyle, height: '60px', resize: 'vertical' }} value={form.description} onChange={set('description')} /></div>
          <div style={row2}>
            <div><label style={labelStyle}>Strengths</label><textarea style={{ ...inputStyle, height: '60px', resize: 'vertical' }} value={form.strengths} onChange={set('strengths')} /></div>
            <div><label style={labelStyle}>Weaknesses</label><textarea style={{ ...inputStyle, height: '60px', resize: 'vertical' }} value={form.weaknesses} onChange={set('weaknesses')} /></div>
          </div>
          <div style={{ marginBottom: '12px' }}><label style={labelStyle}>Competing Products (comma-separated)</label><input style={inputStyle} value={form.product_line_overlap} onChange={set('product_line_overlap')} placeholder="e.g. SIGINT Suite, Sensor Fusion Platform" /></div>
          <div style={{ marginBottom: '20px' }}><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, height: '60px', resize: 'vertical' }} value={form.notes} onChange={set('notes')} /></div>
          {error && <div style={{ color: '#EF4444', fontSize: '12px', marginBottom: '12px' }}>{error}</div>}
          {ingest && (
            <div style={{
              marginBottom: '12px', padding: '10px 12px', borderRadius: '6px',
              border: `1px solid ${ingest.status === 'error' ? '#7F1D1D' : '#1E2D45'}`,
              background: ingest.status === 'error' ? '#7F1D1D22' : '#0D1F3C22',
              color: '#94A3B8', fontSize: '12px', lineHeight: 1.5,
            }}>
              {ingest.status === 'running' && <span>⏳ Importing products from the company website… this runs in the background.</span>}
              {ingest.status === 'done' && (
                <span style={{ color: '#44AA66' }}>
                  ✅ Imported {ingest.products_count ?? 0} product{(ingest.products_count ?? 0) === 1 ? '' : 's'}
                  {ingest.method ? ` (via ${ingest.method})` : ''}. {ingest.total_products ?? ''} total on file.
                </span>
              )}
              {ingest.status === 'error' && <span style={{ color: '#FCA5A5' }}>Import failed: {ingest.message || 'unknown error'}</span>}
              {ingest.status === 'none' && <span>Import queued.</span>}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            {ingest ? (
              <button type="button" onClick={onClose} style={{ padding: '7px 16px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                {ingest.status === 'running' ? 'Close (keep importing)' : 'Close'}
              </button>
            ) : (
              <>
                <button type="button" onClick={onClose} style={{ padding: '7px 16px', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '7px 16px', background: saving ? '#374151' : '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}>{saving ? 'Saving…' : 'Save'}</button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

const selectStyle = { background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 8px', borderRadius: '4px', fontSize: '12px' };

export default function CompetitorView() {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [alignmentFilter, setAlignmentFilter] = useState('');
  const [fundingFilter, setFundingFilter] = useState('');
  const [soeOnly, setSoeOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editCompetitor, setEditCompetitor] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await crmService.getCompetitors({
        alignment: alignmentFilter || undefined,
        soe_only: soeOnly ? 'true' : undefined,
        sort: sort || undefined,
      });
      setCompetitors(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [alignmentFilter, soeOnly, sort]);

  useEffect(() => { load(); }, [load]);

  const handleDeactivate = async (c) => {
    if (!window.confirm(`Remove ${c.name} from the industry list?`)) return;
    try {
      await crmService.updateCompetitor(c.id, { ...c, active: false });
      load();
    } catch (err) { console.error(err); }
  };

  const filtered = competitors.filter(c => {
    if (typeFilter && c.relationship_type !== typeFilter) return false;
    if (fundingFilter && c.funding_stage !== fundingFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.name || '').toLowerCase().includes(q) ||
             (c.acronym || '').toLowerCase().includes(q) ||
             (c.description || '').toLowerCase().includes(q) ||
             (c.country || '').toLowerCase().includes(q);
    }
    return true;
  });

  const byType = Object.fromEntries(
    Object.keys(TYPE_META).map(t => [t, competitors.filter(c => c.relationship_type === t).length])
  );

  const thStyle = { padding: '8px 10px', textAlign: 'left', color: '#6B7280', fontWeight: '500', borderBottom: '1px solid #1E2D45', whiteSpace: 'nowrap', fontSize: '11px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px 16px', backgroundColor: '#111827', borderBottom: '1px solid #1E2D45', alignItems: 'center' }}>
        <input
          type="text" placeholder="Search name, country…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...selectStyle, width: '180px' }}
        />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selectStyle}>
          <option value="">All Types</option>
          {Object.entries(TYPE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label} ({byType[k] || 0})</option>
          ))}
        </select>
        <select value={fundingFilter} onChange={e => setFundingFilter(e.target.value)} style={selectStyle}>
          <option value="">All Funding Stages</option>
          {Object.entries(FUNDING_STAGE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={alignmentFilter} onChange={e => setAlignmentFilter(e.target.value)} style={selectStyle}>
          <option value="">All Alignments</option>
          <option value="five_eyes">Five Eyes</option>
          <option value="nato_ally">NATO</option>
          <option value="friendly">Friendly</option>
          <option value="neutral">Neutral</option>
          <option value="unfriendly">Caution</option>
          <option value="adversary">Adversary</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#94A3B8', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={soeOnly} onChange={e => setSoeOnly(e.target.checked)}
            style={{ accentColor: '#F59E0B', cursor: 'pointer' }} />
          SOE only
        </label>
        <select value={sort} onChange={e => setSort(e.target.value)} style={selectStyle}>
          <option value="">Sort: Type / Name</option>
          <option value="revenue">Sort: Revenue ↓</option>
        </select>

        {/* Threat pills */}
        <div style={{ display: 'flex', gap: '5px' }}>
          {Object.entries(TYPE_META).map(([k, v]) => byType[k] > 0 && (
            <span key={k} style={{ background: v.color + '22', color: v.color, border: `1px solid ${v.color}44`, padding: '2px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: '600' }}>
              {byType[k]} {v.label}
            </span>
          ))}
        </div>

        <button
          onClick={() => { setEditCompetitor(null); setShowModal(true); }}
          style={{ marginLeft: 'auto', padding: '6px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}
        >+ Add Company</button>
      </div>

      {/* Count bar */}
      <div style={{ padding: '5px 16px', backgroundColor: '#0A0F1C', borderBottom: '1px solid #111827' }}>
        <span style={{ color: '#6B7280', fontSize: '11px' }}>
          {filtered.length} compan{filtered.length !== 1 ? 'ies' : 'y'}{(typeFilter || alignmentFilter || soeOnly || search) ? ' (filtered)' : ''}
        </span>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🏭</div>
            <div style={{ fontSize: '14px', color: '#94A3B8', marginBottom: '8px' }}>No companies found</div>
            <button onClick={() => { setEditCompetitor(null); setShowModal(true); }}
              style={{ padding: '8px 20px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
              Add First Company
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#111827', position: 'sticky', top: 0 }}>
                <th style={{ ...thStyle, width: 42 }} />
                <th style={thStyle}>Company</th>
                <th style={thStyle}>Revenue</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Win Rate</th>
                <th style={thStyle}>Notes</th>
                <th style={{ ...thStyle, width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const meta = TYPE_META[c.relationship_type] || TYPE_META.direct;
                const products = Array.isArray(c.product_line_overlap) ? c.product_line_overlap : [];
                const verticals = Array.isArray(c.verticals) ? c.verticals : [];
                const isExpanded = expandedId === c.id;

                return [
                  <tr
                    key={c.id}
                    onClick={() => setExpandedId(isExpanded ? null : c.id)}
                    style={{ borderBottom: isExpanded ? 'none' : '1px solid #111827', cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#0D1520'; }}
                    onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = ''; }}
                  >
                    {/* Logo */}
                    <td style={{ padding: '8px 4px 8px 12px' }}>
                      <CompanyLogo website={c.website} name={c.name} uploadedLogo={c.logo_url} />
                    </td>

                    {/* Name + badges */}
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                        {c.website ? (
                          <a href={c.website} target="_blank" rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ color: '#F1F5F9', fontWeight: '600', textDecoration: 'none', fontSize: '12px' }}
                            onMouseEnter={e => e.target.style.color = '#22D3EE'}
                            onMouseLeave={e => e.target.style.color = '#F1F5F9'}
                          >
                            {c.name}
                          </a>
                        ) : (
                          <span style={{ color: '#F1F5F9', fontWeight: '600' }}>{c.name}</span>
                        )}
                        {c.acronym && <span style={{ color: '#6B7280', fontWeight: '400', fontSize: '11px' }}>({c.acronym})</span>}
                        {c.country_alignment && <AlignmentBadge alignment={c.country_alignment} />}
                        {c.is_state_owned && (
                          <span style={{ background: '#F59E0B22', color: '#F59E0B', border: '1px solid #F59E0B44', padding: '1px 5px', borderRadius: '6px', fontSize: '9px', fontWeight: '700' }}>SOE</span>
                        )}
                      </div>
                      {c.country && (
                        <div style={{ color: '#4B5563', fontSize: '10px' }}>{c.country}</div>
                      )}
                    </td>

                    {/* Revenue */}
                    <td style={{ padding: '8px 10px', color: c.annual_revenue_usd_millions ? '#22D3EE' : '#374151', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      {formatRevenue(c.annual_revenue_usd_millions)}
                    </td>

                    {/* Type + Funding Stage */}
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ background: meta.color + '22', color: meta.color, border: `1px solid ${meta.color}44`, padding: '2px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: '600' }}>
                          {meta.label}
                        </span>
                        {c.funding_stage && FUNDING_STAGE_META[c.funding_stage] && (
                          <span style={{ background: FUNDING_STAGE_META[c.funding_stage].color + '22', color: FUNDING_STAGE_META[c.funding_stage].color, border: `1px solid ${FUNDING_STAGE_META[c.funding_stage].color}44`, padding: '2px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: '600' }}>
                            {FUNDING_STAGE_META[c.funding_stage].label}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Win rate */}
                    <td style={{ padding: '8px 10px' }}>
                      {c.win_rate_against != null ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '50px', height: '5px', borderRadius: '3px', backgroundColor: '#1E2D45', overflow: 'hidden' }}>
                            <div style={{ width: `${c.win_rate_against}%`, height: '100%', backgroundColor: c.win_rate_against >= 60 ? '#10B981' : c.win_rate_against >= 40 ? '#F59E0B' : '#EF4444' }} />
                          </div>
                          <span style={{ fontWeight: '600', fontSize: '11px', color: c.win_rate_against >= 60 ? '#10B981' : c.win_rate_against >= 40 ? '#F59E0B' : '#EF4444' }}>
                            {c.win_rate_against}%
                          </span>
                        </div>
                      ) : <span style={{ color: '#374151' }}>—</span>}
                    </td>

                    {/* Notes */}
                    <td style={{ padding: '8px 10px', color: '#6B7280', maxWidth: '260px' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '11px' }}>
                        {c.market_notes || c.notes || c.description || '—'}
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={e => { e.stopPropagation(); setEditCompetitor(c); setShowModal(true); }}
                        style={{ padding: '3px 8px', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>
                        Edit
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDeactivate(c); }}
                        style={{ padding: '3px 8px', background: 'transparent', border: '1px solid #374151', color: '#6B7280', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                        ✕
                      </button>
                    </td>
                  </tr>,

                  isExpanded && (
                    <tr key={`${c.id}-exp`} style={{ borderBottom: '1px solid #111827', backgroundColor: '#0D1B2A' }}>
                      <td colSpan={7} style={{ padding: '12px 16px 16px 58px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>

                          {/* Verticals */}
                          {verticals.length > 0 && (
                            <div>
                              <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Technology Verticals</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {verticals.map(v => (
                                  <span key={v.id} style={{ background: '#22D3EE14', color: '#22D3EE', border: '1px solid #22D3EE30', padding: '2px 8px', borderRadius: '9px', fontSize: '10px', fontWeight: '500' }}>
                                    {v.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Competing products */}
                          {products.length > 0 && (
                            <div>
                              <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Competing Products</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {products.map(p => (
                                  <span key={p} style={{ background: '#111827', color: '#6B7280', padding: '2px 7px', borderRadius: '3px', fontSize: '11px', border: '1px solid #1E2D45' }}>{p}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {c.description && (
                            <div>
                              <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Description</div>
                              <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: '1.5' }}>{c.description}</div>
                            </div>
                          )}

                          {c.strengths && (
                            <div>
                              <div style={{ color: '#EF4444', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Strengths</div>
                              <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: '1.5' }}>{c.strengths}</div>
                            </div>
                          )}

                          {c.weaknesses && (
                            <div>
                              <div style={{ color: '#10B981', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Weaknesses</div>
                              <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: '1.5' }}>{c.weaknesses}</div>
                            </div>
                          )}

                          {(c.market_notes || c.notes) && (
                            <div>
                              <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Notes</div>
                              <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: '1.5' }}>{c.market_notes || c.notes}</div>
                            </div>
                          )}

                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <CompetitorModal
          competitor={editCompetitor}
          onSuccess={load}
          onClose={() => { setShowModal(false); setEditCompetitor(null); }}
        />
      )}
    </div>
  );
}
